import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faYoutube,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";

/**
 * Professional Footer (TailwindCSS)
 * - Deep navy → charcoal gradient for a corporate look
 * - Clear hierarchy, large tap targets, and strong contrast
 * - Mobile-first with collapsible Products section
 * - Subtle rings and separators for a premium feel
 */

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="relative text-slate-100 bg-gradient-to-b from-[#0f172a] via-[#1e293b] to-[#0b1220]">
      {/* Accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-500" />

      {/* Main content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {/* Top grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {/* About / Know us */}
          <section aria-labelledby="footer-know-us">
            <h3
              id="footer-know-us"
              className="text-lg font-semibold tracking-tight text-white"
            >
              Get to Know Us
            </h3>
            <ul className="mt-4 space-y-2 text-[15px]">
              <li>
                <a
                  href="https://www.ogcs.co.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    About Us
                  </span>
                </a>
              </li>
              <li>
                <Link
                  to="/vision"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Vision
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/mission"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Mission
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/blogPage"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Blogs
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  to="/join-with-us"
                  className="group inline-flex items-center gap-2 text-slate-300 hover:text-white transition"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 group-hover:bg-cyan-300" />
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Join with Us
                  </span>
                </Link>
              </li>
            </ul>
          </section>

          {/* Social */}
          <section aria-labelledby="footer-connect">
            <h3
              id="footer-connect"
              className="text-lg font-semibold tracking-tight text-white"
            >
              Connect with Us
            </h3>
            <ul className="mt-4 space-y-3 text-[15px]">
              <li>
                <a
                  href="https://www.facebook.com/buildpro.ogcs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon
                      icon={faFacebook}
                      className="text-[#1877F2]"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Facebook
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://x.com/BuildproOGCS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon
                      icon={faTwitter}
                      className="text-[#1DA1F2]"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Twitter
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/buildpro.ogcs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon
                      icon={faInstagram}
                      className="text-[#E4405F]"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    Instagram
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/@OGCS_01"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon
                      icon={faYoutube}
                      className="text-[#FF0000]"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    YouTube
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/company/buildpro-ogcs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-slate-300 hover:text-white transition"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-white/5 ring-1 ring-white/10 group-hover:bg-white/10">
                    <FontAwesomeIcon
                      icon={faLinkedin}
                      className="text-[#0077B5]"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="underline decoration-transparent group-hover:decoration-sky-400 underline-offset-4">
                    LinkedIn
                  </span>
                </a>
              </li>
            </ul>
          </section>

          {/* Contact */}
          <section aria-labelledby="footer-contact">
            <h3
              id="footer-contact"
              className="text-lg font-semibold tracking-tight text-white"
            >
              Contact Us
            </h3>
            <address className="mt-4 not-italic text-slate-300 leading-7 text-[15px]">
              <strong className="block text-slate-100">
                OGCS Private Limited
              </strong>
              <span>No 03, C-6, 14, Rd Number 2</span>
              <br />
              <span>Sector 18, New Panvel, Dist: Raigad</span>
              <br />
              <span>Pin Code: 410206</span>
              <br />
              <span className="font-medium text-slate-200">Mobile:</span>{" "}
              <a
                href="tel:+919967610135"
                className="text-sky-300 hover:text-white underline underline-offset-4"
              >
                +91 9967610135
              </a>
              <br />
              <span className="font-medium text-slate-200">Email:</span>{" "}
              <a
                href="mailto:support@ogcs.co.in"
                className="text-sky-300 hover:text-white underline underline-offset-4"
              >
                support@ogcs.co.in
              </a>
            </address>
          </section>
        </div>

        {/* Products (collapsible on mobile) */}
        <section className="mt-10">
          <details className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
            <summary className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-4 cursor-pointer select-none">
              <div className="flex items-center gap-3">
                <span
                  className="grid h-8 w-8 place-items-center rounded-full bg-sky-400/10 ring-1 ring-sky-300/30"
                  aria-hidden="true"
                >
                  🧱
                </span>
                <h4 className="text-base sm:text-lg font-semibold text-white">
                  Products
                </h4>
              </div>
              <span
                className="text-sky-300 transition-transform group-open:rotate-180"
                aria-hidden="true"
              >
                ▾
              </span>
            </summary>

            <div className="px-4 pb-4 sm:px-5 sm:pb-5">
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[14px] leading-6 text-slate-300">
                <li>
                  <strong className="text-slate-100">Cement:</strong> OPC-53
                  Grade Cement, PPC Cement
                </li>
                <li>
                  <strong className="text-slate-100">Sand & Aggregates:</strong>{" "}
                  River Sand, M-Sand, Crushed Aggregates
                </li>
                <li>
                  <strong className="text-slate-100">TMT Steel Bars:</strong>{" "}
                  Fe-500 Grade TMT Bars, Fe-550 Grade TMT Bars, TMT Binding
                  Wire, Rebar Couplers
                </li>
                <li>
                  <strong className="text-slate-100">Bricks & Blocks:</strong>{" "}
                  Concrete Solid Blocks, Flyash Bricks, AAC Blocks, Red Bricks
                </li>
                <li>
                  <strong className="text-slate-100">Electrical:</strong>{" "}
                  Conduit Pipes, Wires & Cables, Modular Switches & Sockets,
                  Electric Panels, Switch Gear (DB/MCB/RCCB), Others
                </li>
                <li>
                  <strong className="text-slate-100">Plumbing:</strong> CPVC
                  Pipes & Fittings, UPVC Pipes & Fittings, SWR Pipes & Fittings,
                  Specials & Accessories
                </li>
                <li>
                  <strong className="text-slate-100">Wooden Products:</strong>{" "}
                  Plywood, Block Boards, Decorative Laminates
                </li>
                <li>
                  <strong className="text-slate-100">Paints & Finishes:</strong>{" "}
                  Wall Care Putty, Decorative Paint Coatings, Texture & Wall
                  Care Finishes
                </li>
                <li>
                  <strong className="text-slate-100">
                    Lighting & Fixtures:
                  </strong>{" "}
                  Indoor Luminaires, Outdoor Luminaires, Lighting Electronics &amp;
                  Controls
                </li>
                <li>
                  <strong className="text-slate-100">
                    UPVC Doors & Windows:
                  </strong>{" "}
                  UPVC Doors, UPVC Windows
                </li>
                <li>
                  <strong className="text-slate-100">
                    Construction Chemicals:
                  </strong>{" "}
                  Admixtures, Sealants, Waterproofing Solutions
                </li>
                <li>
                  <strong className="text-slate-100">
                    Structural Material:
                  </strong>{" "}
                  Structural Steel, MS Plates, Angles, Channels
                </li>
                <li>
                  <strong className="text-slate-100">Formwork Material:</strong>{" "}
                  Shuttering Plywood, Props, Spans, Centering Sheets
                </li>
                <li>
                  <strong className="text-slate-100">
                    Structural Equipment:
                  </strong>{" "}
                  Scaffolding Systems, Column Shutters, Beam Formwork
                </li>
                <li>
                  <strong className="text-slate-100">
                    Construction Material:
                  </strong>{" "}
                  Binding Wire, Nails, GI Sheets, Concrete Mixes
                </li>
                <li>
                  <strong className="text-slate-100">Safety Items:</strong>{" "}
                  Helmets, Gloves, Jackets, Safety Nets, Shoes
                </li>
              </ul>
            </div>
          </details>
        </section>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-slate-400">
            © {year} BuildPro OGCS. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/terms-and-conditions"
              className="text-sky-300 hover:text-white underline underline-offset-4"
            >
              Terms &amp; Conditions
            </Link>
            <a
              href="#top"
              className="text-slate-400 hover:text-white transition"
            >
              Back to top ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
